import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  KPICard,
  DateFilter,
  ActivityFeed,
  PendingActions,
  ImpactSummary,
  NewClientsCard,
  HistoricStrip,
  TrendBars,
  COMPARISON_LABEL,
} from '@/components/admin/dashboard';
import { useDashboardStats, useDashboardTrends } from '@/hooks/useDashboardStats';
import { Skeleton } from '@/components/ui/skeleton';
import { AdminPageShell, PageHeader, PageError } from '@/components/admin/shell';
import type { DashboardFilter, TrendGranularity } from '@/types/dashboard';

/**
 * Dashboard de impacto (specs 007-dashboard-impacto y 009-dashboard-camino)
 *
 * Orden: portada con el camino del comensal → evolución, clientes nuevos e histórico → operación.
 */
export function DashboardPage() {
  const [filter, setFilter] = useState<DashboardFilter>('this_month');
  const [granularity, setGranularity] = useState<TrendGranularity>('month');
  const { data: stats, isLoading, error, refetch } = useDashboardStats(filter);
  const { data: trends, isLoading: trendsLoading } = useDashboardTrends(granularity);

  if (error) {
    return (
      <AdminPageShell>
        <PageError message="Error al cargar estadísticas" onRetry={() => refetch()} />
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell>
      <div className="space-y-6">
        <PageHeader title="Dashboard">
          <DateFilter value={filter} onChange={setFilter} />
        </PageHeader>

        {isLoading || !stats ? (
          <div className="space-y-4">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-56" />
          </div>
        ) : (
          <ImpactSummary
            filter={filter}
            current={stats.current}
            previous={stats.previous}
            economics={stats.economics}
          />
        )}

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {trendsLoading || !trends ? (
              <Skeleton className="h-72" />
            ) : (
              <TrendBars trends={trends} granularity={granularity} onGranularityChange={setGranularity} />
            )}
          </div>
          {stats && (
            <div className="space-y-6">
              <NewClientsCard
                current={stats.current.new_clients}
                previous={stats.previous.new_clients}
                comparison={COMPARISON_LABEL[filter]}
              />
              <HistoricStrip totals={stats.totals} />
            </div>
          )}
        </div>

        {stats && (
          <section className="space-y-4" aria-labelledby="operations-title">
            <h2 id="operations-title" className="text-[16px] font-light text-[#1e1e1e]">
              Operación
            </h2>
            <div className="grid gap-6 lg:grid-cols-3">
              <div className="rounded-sm border border-gray-200 bg-white p-6 lg:col-span-2">
                <h3 className="mb-4 text-sm font-light text-[#1e1e1e]">Actividad reciente</h3>
                <ActivityFeed memories={stats.operations.recent_memories} />
              </div>
              <div className="rounded-sm border border-gray-200 bg-white p-6">
                <h3 className="mb-4 text-sm font-light text-[#1e1e1e]">Acciones pendientes</h3>
                <PendingActions
                  pendingOrders={stats.operations.pending_orders}
                  itemsWithoutPhotos={stats.operations.items_without_photos}
                  videosProcessing={stats.operations.videos_processing}
                />
              </div>
            </div>
            {stats.operations.items_without_photos > 0 && (
              <KPICard
                title="Items sin fotos"
                value={stats.operations.items_without_photos}
                icon={AlertTriangle}
                iconColor="text-amber-600"
                link="/admin/items"
                warning
                subtitle="Revisa el catálogo"
              />
            )}
          </section>
        )}
      </div>
    </AdminPageShell>
  );
}
